import { setTitle } from "app/store/transmission/slice/transmissionSlice";
import {
  enrollPatient,
  mergePatientRecords,
} from "app/services/patient/patient";
import {
  patientApi,
  useDownloadDisconnectedMonitorsListMutation,
  useDownloadPatientListMutation,
  useDownloadReminderPatientListMutation,
  useUpdatePatientMutation,
} from "app/store/patient/api/patientApi";
import {
  toggleFetchTableData,
  togglePatientEnrollModal,
} from "app/store/patient/slice/patientSlice";
import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { showErrorToast, showSuccessToast } from "utils/toast";
import { downloadFile } from "app/utils/file";
import { transmissionTableApi } from "app/store/transmission/api/transmissionTableApi";

const usePatients = (filters, sorting) => {
  const { isPatientModal } = useSelector((state) => state.transmission);
  const { page, pageSize, searchText, isPatientEnrollModal } = useSelector(
    (state) => state.patient
  );
  const [showReminderFilter, setShowReminderFilter] = useState(false);
  const [isMerging, setIsMerging] = useState(false);

  const dispatch = useDispatch();

  const [showDisconnectMonitorModal, setShowDisconnectMonitorModal] =
    useState(false);

  const [showMergePatientModal, setShowMergePatientModal] = useState(false);

  useEffect(() => {
    dispatch(setTitle({ title: "Patients" }));
  }, [dispatch]);

  const toggleEnrollPatientModal = (open) => {
    dispatch(togglePatientEnrollModal(open));
  };

  const [updatePatient, { isLoading: isUpdatingPatient }] =
    useUpdatePatientMutation();

  const updatePatientById = async (patientId, payload) => {
    try {
      const response = await updatePatient({ patientId, payload }).unwrap();

      if (!response?.error) {
        showSuccessToast(response?.message);
        await dispatch(toggleFetchTableData(true));
      }
    } catch (err) {
      showErrorToast(err?.message);
    } finally {
      dispatch(toggleFetchTableData(false));
    }
  };

  const [downloadPatientList, { isLoading: isDownloading }] =
    useDownloadPatientListMutation();

  const [
    downloadDisconnectedMonitors,
    { isLoading: isDownloadingDisconnectedMonitors },
  ] = useDownloadDisconnectedMonitorsListMutation();

  const [
    downloadReminderPatientList,
    { isLoading: isDownloadReminderPatientList },
  ] = useDownloadReminderPatientListMutation();

  const downloadPatients = async () => {
    try {
      const query = {
        page,
        pageSize,
        filters,
        sorting,
        search: searchText,
      };
      const result = await downloadPatientList(query).unwrap();

      if (result) {
        showSuccessToast("Patients list successfully downloaded.");
      }
    } catch (error) {
      showErrorToast(error.message);
    }
  };

  const downloadDisconnectedMonitor = async (payload, downloadType) => {
    try {
      const response = await downloadDisconnectedMonitors({
        payload,
        downloadType,
      });
      if (response.error) {
        throw response.error;
      }
      const { filename } = response.data;
      downloadFile(response.data, filename);
      setShowDisconnectMonitorModal(false);
    } catch (error) {
      showErrorToast(error.message);
    }
  };

  const downloadReminderPatients = async (values) => {
    try {
      const result = await downloadReminderPatientList(values).unwrap();

      if (result) {
        showSuccessToast("Reminder patients successfully downloaded.");
      }
    } catch (error) {
      showErrorToast(error.message);
    }
  };

  const handlePatientRecordMerge = async ({
    selectedPatientRecord: primaryPatientRecord,
    remainingPatientRecords,
  }) => {
    try {
      setIsMerging(true);
      const primaryPatientEvokeId = primaryPatientRecord?.evoke_id;
      const duplicatedPatientEvokeIds = remainingPatientRecords.map(
        ({ evoke_id }) => evoke_id
      );

      const payload = { primaryPatientEvokeId, duplicatedPatientEvokeIds };
      const result = await mergePatientRecords(payload);

      if (result) {
        showSuccessToast(result?.message);
        dispatch(patientApi.util.invalidateTags(["patientList"]));
        dispatch(transmissionTableApi.util.invalidateTags("transmissions"));
      }
    } catch (error) {
      showErrorToast(error?.message);
    } finally {
      setIsMerging(false);
    }
  };

  return {
    isMerging,
    isDownloading,
    isPatientModal,
    isUpdatingPatient,
    showReminderFilter,
    isPatientEnrollModal,
    showMergePatientModal,
    showDisconnectMonitorModal,
    isDownloadReminderPatientList,
    isDownloadingDisconnectedMonitors,

    dispatch,
    enrollPatient,
    downloadPatients,
    updatePatientById,
    setShowReminderFilter,
    setShowMergePatientModal,
    toggleEnrollPatientModal,
    downloadReminderPatients,
    handlePatientRecordMerge,
    downloadDisconnectedMonitor,
    setShowDisconnectMonitorModal,
  };
};

export default usePatients;
